import {
  Briefcase,
  CheckCircle2,
  ClipboardList,
  FileCheck,
  Heart,
  MessageSquare,
  Stethoscope,
} from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';

import {
  CaseCategory,
  DemandReadiness,
  DemandType,
  MostSignificantCare,
} from '../matter-form/DemandReadinessModal';

interface DemandReadinessCardProps {
  demandReadiness: DemandReadiness | null | undefined;
}

const CASE_CATEGORY_CONFIG: Record<
  CaseCategory,
  { label: string; color: string; bgColor: string; borderColor: string }
> = {
  [CaseCategory.HIGH_VALUE]: {
    label: 'High Value',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
  },
  [CaseCategory.FULL_POLICY_LIMITS]: {
    label: 'Full Policy Limits',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  [CaseCategory.SOFT_TISSUE]: {
    label: 'Soft Tissue',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
};

const DEMAND_TYPE_CONFIG: Record<
  DemandType,
  { label: string; description: string }
> = {
  [DemandType.FIRST_PARTY]: {
    label: '1P',
    description: 'First Party (UM/UIM)',
  },
  [DemandType.THIRD_PARTY]: {
    label: '3P',
    description: 'Third Party Liability',
  },
  [DemandType.MEDPAY]: {
    label: 'Medpay',
    description: 'Medical Payments Coverage',
  },
  [DemandType.PIP]: {
    label: 'PIP',
    description: 'Personal Injury Protection',
  },
};

const CARE_CONFIG: Record<
  MostSignificantCare,
  { label: string; color: string; icon: typeof Stethoscope }
> = {
  [MostSignificantCare.CONSERVATIVE]: {
    label: 'Conservative',
    color: 'text-green-600',
    icon: Stethoscope,
  },
  [MostSignificantCare.SURGICAL_RECOMMENDATION]: {
    label: 'Surgical Recommendation',
    color: 'text-amber-600',
    icon: ClipboardList,
  },
  [MostSignificantCare.SURGICAL_PROCEDURE]: {
    label: 'Surgical Procedure',
    color: 'text-red-600',
    icon: Heart,
  },
};

export const DemandReadinessCard: React.FC<DemandReadinessCardProps> = ({
  demandReadiness,
}) => {
  if (!demandReadiness) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-400">
        <div className="text-center">
          <FileCheck className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No demand readiness information</p>
        </div>
      </div>
    );
  }

  const categoryConfig = CASE_CATEGORY_CONFIG[demandReadiness.case_category];
  const demandTypeConfig = DEMAND_TYPE_CONFIG[demandReadiness.demand_type];
  const careConfig = CARE_CONFIG[demandReadiness.most_significant_care];
  const CareIcon = careConfig?.icon || Stethoscope;

  return (
    <div className="space-y-5">
      {/* Bills/Records Confirmation Status */}
      <div
        className={cn(
          'flex items-center gap-3 p-4 rounded-xl border',
          demandReadiness.bills_records_confirmed
            ? 'bg-green-50/50 border-green-200'
            : 'bg-red-50/50 border-red-200',
        )}
      >
        <div
          className={cn(
            'flex items-center justify-center w-10 h-10 rounded-full',
            demandReadiness.bills_records_confirmed
              ? 'bg-green-100'
              : 'bg-red-100',
          )}
        >
          {demandReadiness.bills_records_confirmed ? (
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          ) : (
            <FileCheck className="h-5 w-5 text-red-600" />
          )}
        </div>
        <div>
          <p
            className={cn(
              'font-medium',
              demandReadiness.bills_records_confirmed
                ? 'text-green-700'
                : 'text-red-700',
            )}
          >
            {demandReadiness.bills_records_confirmed
              ? 'All Bills & Records Confirmed'
              : 'Bills & Records Not Confirmed'}
          </p>
          <p className="text-xs text-gray-500">
            {demandReadiness.bills_records_confirmed
              ? 'All documentation has been received and is in file'
              : 'Documentation may be incomplete'}
          </p>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-3 gap-4">
        {/* Case Category */}
        <div
          className={cn(
            'rounded-xl p-4 border',
            categoryConfig?.bgColor || 'bg-gray-50',
            categoryConfig?.borderColor || 'border-gray-200',
          )}
        >
          <div className="flex items-center gap-2 mb-2">
            <Briefcase
              className={cn(
                'h-4 w-4',
                categoryConfig?.color || 'text-gray-600',
              )}
            />
            <span className="text-xs font-medium text-gray-600">
              Case Category
            </span>
          </div>
          <p
            className={cn(
              'text-lg font-semibold',
              categoryConfig?.color || 'text-gray-700',
            )}
          >
            {categoryConfig?.label || demandReadiness.case_category}
          </p>
        </div>

        {/* Demand Type */}
        <div className="rounded-xl p-4 bg-slate-50/50 border border-slate-200">
          <div className="flex items-center gap-2 mb-2">
            <FileCheck className="h-4 w-4 text-slate-600" />
            <span className="text-xs font-medium text-gray-600">
              Demand Type
            </span>
          </div>
          <p className="text-lg font-semibold text-slate-700">
            {demandTypeConfig?.label || demandReadiness.demand_type}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {demandTypeConfig?.description}
          </p>
        </div>

        {/* Most Significant Care */}
        <div className="rounded-xl p-4 bg-gray-50/50 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <CareIcon
              className={cn('h-4 w-4', careConfig?.color || 'text-gray-600')}
            />
            <span className="text-xs font-medium text-gray-600">
              Significant Care
            </span>
          </div>
          <p
            className={cn(
              'text-lg font-semibold',
              careConfig?.color || 'text-gray-700',
            )}
          >
            {careConfig?.label || demandReadiness.most_significant_care}
          </p>
        </div>
      </div>

      {/* Special Instructions */}
      {demandReadiness.special_instructions && (
        <div className="rounded-xl p-4 bg-yellow-50/50 border border-yellow-200">
          <div className="flex items-start gap-3">
            <MessageSquare className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-yellow-800 mb-1">
                Special Instructions
              </p>
              <p className="text-sm text-yellow-700 whitespace-pre-wrap">
                {demandReadiness.special_instructions}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemandReadinessCard;
